/**
 * THE SKIP OR MOVE SHEET, drawn over Schedule in `faithfully-kept-business/design/home/
 * Schedule.dc.html`: the visit's day and line, "Skip this visit", "Ask to move it", then which days
 * would suit, the day chips and "Send the request", and that nothing changes until we confirm.
 * With several homes, which no artboard draws, the visit's home is named under the day, as its
 * card names it, so a skip of one of two cleans on a day is never read as the other's.
 *
 * - A SKIP IS SENT AT ONCE. A move opens the question of which days would suit, and sends the days
 *   chosen, one or more, in the calendar's order. The days offered are `daysThatMightSuit`'s,
 *   pending the owner. The artboard draws no note and no window, so neither is sent.
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). A request the door has written closes the
 *   sheet and is shown on the card as sent to the office; the visit changes only when ops pushes it
 *   changed, which is Schedule's to draw from the next read.
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED, AND HOWEVER OFTEN THE SHEET IS CLOSED. A key is minted
 *   once for what is asked, a skip once and a move once for each set of days, so a retry after a
 *   failure sends the same key and the door writes one row. While one is out, another tap does
 *   nothing. Both are held by Schedule for the visit (`lib/asks.ts`), never by the sheet, so a sheet
 *   closed while a request is out loses nothing, and one opened again carries on where it was.
 * - WHAT THE DOOR ANSWERS decides what follows, whether or not the sheet is still open: sent is
 *   Schedule's to show; a visit no longer open says so here, in words for the owner and with the
 *   phone, and naming no request, because the door wrote none, takes the focus, since the visit
 *   read again offers no way to ask and the button pressed goes, and has Schedule read itself
 *   again; a 404, the visit gone or not hers to ask about now, which the door answers alike, says
 *   the same, in the same words, and the sheet stays open on it even where the read no longer holds
 *   the visit (Schedule keeps it drawn, offering no way to ask), never closing in silence; a 401 is
 *   the way to the door; a skip or a move already waiting reads the place again and closes the
 *   sheet, whose card then says where that one stands; anything else says it did not go through,
 *   with the phone, and leaves the sheet as it was to try again. What the sheet says, it says only
 *   while it is open.
 * - A DIALOG, drawn in the sheet (`Sheet.tsx`): named by the visit's day, which takes the focus
 *   when it opens; the page behind it is inert (`Shell`'s `over`); and it closes on Escape, on its
 *   close button, the X drawn on Feedback, or on a tap outside it.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { sendRequest, type ScheduledVisit, type TooMany } from '../api.ts';
import { keyFor, type Asking } from '../lib/asks.ts';
import { dayInWords, daysThatMightSuit, shortDay } from '../lib/dates.ts';
import { visitLine } from '../lib/visits.ts';
import {
  FAILED_BEFORE_PHONE,
  NOT_OPEN_BEFORE_PHONE,
  SCHEDULE,
  tooManyBeforePhone,
} from '../words.ts';
import { GHOST_BUTTON, NAVY_BUTTON } from './buttons.ts';
import { Phone } from './Phone.tsx';
import { Sheet } from './Sheet.tsx';

interface MoveSheetProps {
  readonly visit: ScheduledVisit;
  /** The name of the visit's home, when she sees several homes (`homeNameOf`). */
  readonly home?: string | undefined;
  /** The business's day, from the place, which the days offered are counted from. */
  readonly today: string;
  /** The household the place shows, which the request is about. */
  readonly household: string;
  /** Whether a request about the visit is out, and its keys, held by Schedule for the visit. */
  readonly asking: Asking;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  /** The door wrote the request, whose id this is. */
  readonly onSent: (id: string) => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What the place shows may be out of date: read it again. */
  readonly onStale: () => void;
}

/**
 * What the sheet says after an ask that was not sent: the visit can no longer be changed, past the
 * door's own limits, or a failure.
 */
type Problem = 'not_open' | 'too_many' | 'failed';

/**
 * The artboard's check, 20px on "Ask to move it" and 18px on a chosen day, and on the view pressed
 * on Schedule's switch, List or Month.
 */
export function Check({ size }: { size: 18 | 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.5 10.5 8.5 14.5 15.5 6" />
    </svg>
  );
}

const CHIP =
  'flex min-h-tap items-center justify-center gap-1.5 rounded-control border-2 px-2 text-small';
const CHIP_CHOSEN = 'border-surface-brand bg-surface-brand-soft font-semibold text-text-body';
const CHIP_FREE =
  'border-border-strong bg-surface-raised text-text-body hover:bg-surface-accent-soft';

export function MoveSheet({
  visit,
  home,
  today,
  household,
  asking,
  onClose,
  onSent,
  onSignedOut,
  onStale,
}: MoveSheetProps) {
  const questionId = useId();
  const [moving, setMoving] = useState(false);
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [problem, setProblem] = useState<Problem | null>(null);
  /** Whose count of the door's own limits it was past, and when to try again, as the door said. */
  const [limit, setLimit] = useState<TooMany>();
  /** Whether this sheet is still open, and so has anything to say. */
  const mounted = useRef(true);
  /** What the sheet says after an ask that was not sent. */
  const problemLine = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A visit no longer open is read again, and offers neither way to ask from then on, so the button
  // that had the focus goes. The focus goes to what the sheet says instead, which stays, so it is
  // read out and stays in the sheet, where Escape still closes it.
  useEffect(() => {
    if (problem === 'not_open') problemLine.current?.focus();
  }, [problem]);

  const days = useMemo(() => daysThatMightSuit(visit.date ?? '', today), [visit.date, today]);
  const canSkip = visit.offers.includes('visit.skip');
  const canMove = visit.offers.includes('visit.move') && days.length > 0;
  const line = visitLine(visit, today);

  /** Asks, once, for what `asked` names: the type, and for a move the days. */
  async function ask(type: 'visit.skip' | 'visit.move', asked: string, payload: object) {
    if (asking.out) return;
    asking.out = true;
    setProblem(null);
    const answer = await sendRequest({
      key: keyFor(asking, asked),
      type,
      household,
      payload: { visit_id: visit.id, ...payload },
    });
    asking.out = false;
    switch (answer.outcome) {
      case 'sent':
        asking.keys.clear();
        onSent(answer.id);
        return;
      case 'signed_out':
        onSignedOut();
        return;
      // A skip or a move of the visit already waits on the office: asking again would be refused
      // again, so the schedule is read again, and its card says where that one stands.
      case 'already_asked':
        onStale();
        if (mounted.current) onClose();
        return;
      // The visit is gone, or not hers to ask about now, which the door answers alike (binding rule
      // 2): said in the words of a visit no longer open, never silent, and the place read again.
      case 'not_found':
      case 'not_open':
        if (mounted.current) setProblem('not_open');
        onStale();
        return;
      case 'too_many':
        if (!mounted.current) return;
        setLimit(answer.limit);
        setProblem('too_many');
        return;
      default:
        if (mounted.current) setProblem('failed');
    }
  }

  const skip = () => void ask('visit.skip', 'visit.skip', {});

  const move = () => {
    const suit = [...chosen].sort();
    void ask('visit.move', `visit.move ${suit.join(' ')}`, { days_that_suit: suit });
  };

  const toggle = (day: string) =>
    setChosen((held) =>
      held.includes(day) ? held.filter((each) => each !== day) : [...held, day],
    );

  return (
    <Sheet
      heading={visit.date === undefined ? null : dayInWords(visit.date)}
      sub={
        home === undefined && !line ? undefined : (
          <>
            {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
            {line ? <p className="text-small text-balance text-text-muted">{line}</p> : null}
          </>
        )
      }
      onClose={onClose}
    >
      <div className="mt-5 flex flex-col gap-3">
        {canSkip ? (
          <button type="button" onClick={skip} className={GHOST_BUTTON}>
            {SCHEDULE.skip}
          </button>
        ) : null}
        {canMove ? (
          <button
            type="button"
            aria-expanded={moving}
            onClick={() => setMoving((held) => !held)}
            className={`gap-2 ${NAVY_BUTTON}`}
          >
            {moving ? <Check size={20} /> : null}
            <span>{SCHEDULE.move}</span>
          </button>
        ) : null}
      </div>

      {canMove && moving ? (
        <>
          <p id={questionId} className="mt-6 text-body font-semibold text-text-body">
            {SCHEDULE.whichDays}
          </p>
          <div role="group" aria-labelledby={questionId} className="mt-3 grid grid-cols-2 gap-2">
            {days.map((day) => {
              const on = chosen.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(day)}
                  className={`${CHIP} ${on ? CHIP_CHOSEN : CHIP_FREE}`}
                >
                  {on ? <Check size={18} /> : null}
                  <span>{shortDay(day)}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            disabled={chosen.length === 0}
            onClick={move}
            className={`mt-6 ${NAVY_BUTTON} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {SCHEDULE.send}
          </button>
        </>
      ) : null}

      <div aria-live="polite">
        {problem === null ? null : (
          <p ref={problemLine} tabIndex={-1} className="mt-4 text-body text-pretty">
            {problem === 'not_open'
              ? NOT_OPEN_BEFORE_PHONE
              : problem === 'too_many'
                ? tooManyBeforePhone(limit)
                : FAILED_BEFORE_PHONE}{' '}
            <Phone after="." />
          </p>
        )}
      </div>
      <p className="mt-3 text-small text-pretty text-text-muted">{SCHEDULE.nothingChanges}</p>
    </Sheet>
  );
}
